/**
 * Graph Service
 * Resolves Azure Entra ID object IDs to display names via the backend.
 * Adapted to this app's `POST /graph/displaynames` endpoint (which returns
 * `{ displayNames: { [objectId]: displayName } }`).
 */
import { api } from '../api/client';

export interface UserInfo {
  id: string;
  displayName: string;
  userPrincipalName: string;
  mail: string;
  isUser?: boolean;
  error?: boolean;
}

export interface UserMap {
  [objectId: string]: UserInfo;
}

/**
 * Resolve multiple object IDs to user information.
 * @param objectIds - Array of Azure Entra ID object IDs (GUIDs)
 * @returns Map of objectId -> user information
 */
export const resolveObjectIds = async (objectIds: string[]): Promise<UserMap> => {
  if (!objectIds || objectIds.length === 0) {
    return {};
  }

  const fallback = (): UserMap => {
    const map: UserMap = {};
    objectIds.forEach((id) => {
      map[id] = { id, displayName: id, userPrincipalName: id, mail: id, error: true };
    });
    return map;
  };

  try {
    const response = await api.post<{ displayNames: Record<string, string> }>(
      '/graph/displaynames',
      { userIds: objectIds }
    );
    const displayNames = response.data?.displayNames || {};
    const displayNamesById = new Map(
      Object.entries(displayNames).map(([id, name]) => [id.toLowerCase(), name])
    );

    const map: UserMap = {};
    objectIds.forEach((id) => {
      const name = displayNamesById.get(id.toLowerCase());
      map[id] = name
        ? { id, displayName: name, userPrincipalName: name, mail: name }
        : { id, displayName: id, userPrincipalName: id, mail: id, error: true };
    });
    return map;
  } catch (error) {
    console.error('Error resolving object IDs:', error);
    return fallback();
  }
};

/**
 * Extract object IDs (GUIDs) from a string.
 */
export const extractObjectIdsFromText = (text: string): string[] => {
  if (!text) return [];
  const guidRegex = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
  const matches = text.match(guidRegex);
  return matches ? Array.from(new Set(matches)) : [];
};
